import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { apiService, extractData, handleApiError } from '../services/api';

// Generic hook for API calls with loading states
export const useApi = (apiCall, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedApiCall = useCallback(apiCall, dependencies);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await memoizedApiCall();
        const result = extractData(response);
        setData(result);
      } catch (err) {
        setError(handleApiError(err));
        console.error('API Hook Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [memoizedApiCall]);

  const refetch = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiCall();
      const result = extractData(response);
      setData(result);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refetch };
};

// Hook for articles (all articles)
export const useArticles = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.articles.getAll();
      const result = extractData(response);
      setData(result);
    } catch (err) {
      setError(handleApiError(err));
      console.error('Articles API Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Auto-refetch when page becomes visible (user switches back to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchArticles();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchArticles]);

  return { data, loading, error, refetch: fetchArticles };
};

// Hook for single article by slug
export const useArticle = (slug) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchArticle = useCallback(async () => {
    if (!slug) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/articles/${slug}/`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch article');
      console.error('Article API Error:', err);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  // Auto-refetch when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && slug) {
        fetchArticle();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchArticle, slug]);

  return { data, loading, error, refetch: fetchArticle };
};

// Hook for articles by tag
export const useArticlesByTag = (tag) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tag) return;
    
    const fetchArticlesByTag = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.articles.getByTag(tag);
        const result = extractData(response);
        setData(result);
      } catch (err) {
        setError(handleApiError(err));
        console.error('Articles by tag API Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticlesByTag();
  }, [tag]);

  return { data, loading, error };
};

// Hook for team members
export const useTeam = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.team.getAll();
        const result = extractData(response);
        setData(result);
      } catch (err) {
        setError(handleApiError(err));
        console.error('Team API Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, []);

  return { data, loading, error };
};

// Hook for single team member
export const useTeamMember = (id) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    
    const fetchTeamMember = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.team.getById(id);
        const result = extractData(response);
        setData(result);
      } catch (err) {
        setError(handleApiError(err));
        console.error('Team member API Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMember();
  }, [id]);

  return { data, loading, error };
};

// Hook for site settings
export const useSettings = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.settings.get();
        const result = extractData(response);
        setData(result);
      } catch (err) {
        setError(handleApiError(err));
        console.error('Settings API Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  return { data, loading, error };
};

// Hook for tags
export const useTags = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.tags.getAll();
      const result = extractData(response);
      setData(result);
    } catch (err) {
      setError(handleApiError(err));
      console.error('Tags API Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Auto-refetch when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchTags();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchTags]);

  return { data, loading, error, refetch: fetchTags };
};

// Hook for stats
export const useStats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.stats.get();
        const result = extractData(response);
        setData(result);
      } catch (err) {
        setError(handleApiError(err));
        console.error('Stats API Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { data, loading, error };
};

// Hook for contact form submission
export const useContactForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const submitForm = async (formData) => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(false);
      
      await apiService.contact.submit(formData);
      setSuccess(true);
      
      // Reset success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
      
    } catch (err) {
      setError(handleApiError(err, 'Failed to send message. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetStatus = () => {
    setSuccess(false);
    setError(null);
  };

  return { 
    submitForm, 
    submitting, 
    success, 
    error, 
    resetStatus 
  };
};

// Hook for loading multiple API calls at once
export const useMultipleApi = (apiCalls) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const promises = Object.entries(apiCalls).map(async ([key, apiCall]) => {
          const response = await apiCall();
          return [key, extractData(response)];
        });
        
        const results = await Promise.all(promises);
        const dataObject = Object.fromEntries(results);
        setData(dataObject);
        
      } catch (err) {
        setError(handleApiError(err));
        console.error('Multiple API Hook Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [apiCalls]);

  return { data, loading, error };
};

// Hook for all projects
export const useProjects = (filters = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Build query parameters
        const params = new URLSearchParams();
        if (filters.category) params.append('category', filters.category);
        if (filters.search) params.append('search', filters.search);
        
        const queryString = params.toString();
        const url = queryString ? `/api/projects/?${queryString}` : '/api/projects/';
        
        const response = await axios.get(url);
        setData(response.data);
      } catch (err) {
        setError(handleApiError(err));
        console.error('Projects API Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [filters.category, filters.search]);

  return { data, loading, error };
};

// Hook for single project by slug
export const useProject = (slug) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    
    const fetchProject = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`/api/projects/${slug}/`);
        setData(response.data);
      } catch (err) {
        setError(handleApiError(err));
        console.error('Project API Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [slug]);

  return { data, loading, error };
};

// Hook for featured project
export const useFeaturedProject = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeaturedProject = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get('/api/projects/featured/');
        // API returns array, get first item
        setData(response.data?.[0] || null);
      } catch (err) {
        setError(handleApiError(err));
        console.error('Featured Project API Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProject();
  }, []);

  return { data, loading, error };
};

// Hook for project statistics
export const useProjectStats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjectStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get('/api/projects/stats/');
        setData(response.data);
      } catch (err) {
        setError(handleApiError(err));
        console.error('Project Stats API Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectStats();
  }, []);

  return { data, loading, error };
};